import React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

function getTechnicianNotes(tech = {}) {
  const notes = [];

  if (
    tech.competency_level?.driving_4_wheels &&
    tech.competency_level.driving_4_wheels !== "N/A"
  ) {
    notes.push("Driver: 4 wheels");
  }

  if (
    tech.competency_level?.driving_2_wheels &&
    tech.competency_level.driving_2_wheels !== "N/A"
  ) {
    notes.push("Driver: 2 wheels");
  }

  return notes;
}

function getTechnicianDisplayName(tech = {}) {
  return `${tech.firstname || ""} ${tech.lastname || ""}`.trim();
}

function EditTechniciansDialog({
  open,
  onClose,
  onSave,
  editedTechs = [],
  techniciansList = [],
  onChangeTech,
  onAddTech,
  onRemoveTech,
  isLoading = false,
}) {
  const selectedIds = editedTechs.filter(Boolean);

  const getSelectedTech = (techId) =>
    techniciansList.find((tech) => tech.id === techId) || null;

  const isTechDisabledForRow = (techId, currentRowIndex) => {
    return editedTechs.some(
      (selectedId, index) =>
        index !== currentRowIndex && selectedId && selectedId === techId
    );
  };

  const canAddMore = selectedIds.length < techniciansList.length;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Technicians</DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Update the technician roster assigned to this report before submission.
        </Typography>

        <Grid container spacing={2}>
          {editedTechs.length > 0 ? (
            editedTechs.map((techId, index) => {
              const selectedTech = getSelectedTech(techId);
              const notes = selectedTech ? getTechnicianNotes(selectedTech) : [];

              return (
                <Grid item xs={12} key={`${techId || "empty"}-${index}`}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      p: 1.5,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      backgroundColor: "background.paper",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "flex-start",
                      }}
                    >
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label={`Technician ${index + 1}`}
                        value={techId || ""}
                        onChange={(e) => onChangeTech(index, e.target.value)}
                        disabled={isLoading}
                        SelectProps={{
                          renderValue: (selected) => {
                            const tech = getSelectedTech(selected);
                            if (!tech) return "";

                            const techNotes = getTechnicianNotes(tech);

                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  py: 0.25,
                                }}
                              >
                                <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                                  {getTechnicianDisplayName(tech)}
                                </Typography>

                                {techNotes.length > 0 && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ lineHeight: 1.2 }}
                                  >
                                    {techNotes.join(" • ")}
                                  </Typography>
                                )}
                              </Box>
                            );
                          },
                        }}
                      >
                        {techniciansList.map((tech) => {
                          const techNotes = getTechnicianNotes(tech);
                          const disabled = isTechDisabledForRow(tech.id, index);

                          return (
                            <MenuItem
                              key={tech.id}
                              value={tech.id}
                              disabled={disabled}
                              sx={{
                                alignItems: "flex-start",
                                py: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  width: "100%",
                                }}
                              >
                                <Typography variant="body2">
                                  {getTechnicianDisplayName(tech)}
                                </Typography>

                                {techNotes.length > 0 ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {techNotes.join(" • ")}
                                  </Typography>
                                ) : (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Driving skill not available
                                  </Typography>
                                )}
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => onRemoveTech(index)}
                        disabled={editedTechs.length <= 1 || isLoading}
                        sx={{ whiteSpace: "nowrap", minWidth: 90, mt: "1px" }}
                      >
                        Remove
                      </Button>
                    </Box>

                    {selectedTech && (
                      <Box sx={{ pl: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mb: 0.75 }}
                          color="text.secondary"
                        >
                          {selectedTech.lastname}, {selectedTech.firstname}
                        </Typography>

                        {notes.length > 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.75,
                            }}
                          >
                            {notes.map((note) => (
                              <Chip
                                key={`${selectedTech.id}-${note}`}
                                label={note}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                No technicians selected.
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <Button
              variant="outlined"
              onClick={onAddTech}
              disabled={isLoading || !canAddMore}
            >
              Add Technician
            </Button>

            {!canAddMore && techniciansList.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.75 }}
              >
                All available technicians are already in the roster.
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={onSave} variant="contained" disabled={isLoading}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditTechniciansDialog;